import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectWysiwigEditorEnabled } from 'core/userContext/selectors';
import { HtmlEditor } from 'modules/graphql/types';
import { SideBySideEditor } from 'modules/sideBySideEditor';
import { WYSIWYGEditor } from 'modules/wysiwygEditor';
import { BANNER_VARIANT_ICONS, Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { Link } from 'ui/links';

import { useTranslations } from '../hooks/useTranslations';

type Props = {
  initialValue: string;
  lastSavedValue: string;
  onChange: (content: string, isFirstValue?: boolean) => void;
  value: string;
};

const StyledBanner = styled(Banner)`
  margin-bottom: ${({ theme }) => theme.spacing.half};
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;

export const SnippetContentEditor = ({ initialValue, lastSavedValue, onChange, value }: Props) => {
  const { t } = useTranslations();
  const wysiwygEditorEnabled = useSelector(selectWysiwigEditorEnabled);

  const [contentEditor, setContentEditor] = useState<HtmlEditor>(HtmlEditor.Ace);
  const canSwitchEditor = !!wysiwygEditorEnabled;

  const renderEditorSelector = () => {
    return (
      <>
        {canSwitchEditor && (
          <StyledBanner
            variant={
              contentEditor === HtmlEditor.Ace ? BannerVariants.NEUTRAL : BannerVariants.WARNING
            }
            Icon={
              contentEditor === HtmlEditor.Ace
                ? BANNER_VARIANT_ICONS[BannerVariants.NEUTRAL]
                : BANNER_VARIANT_ICONS[BannerVariants.WARNING]
            }
            BannerActions={
              <StyledButton
                data-test={'snippet-editor-switch-editor'}
                onClick={() => {
                  setContentEditor(
                    contentEditor === HtmlEditor.Ace ? HtmlEditor.Wysiwyg : HtmlEditor.Ace,
                  );
                  // revert the value to last saved
                  onChange(lastSavedValue, true);
                }}
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                {contentEditor === HtmlEditor.Ace
                  ? t('button.switchToWysiwyg')
                  : t('button.switchToSideBySide')}
              </StyledButton>
            }
          >
            <span>
              {contentEditor === HtmlEditor.Ace
                ? t('banner.switchToWysiwyg')
                : t('banner.switchToSideBySide')}{' '}
              <Link
                hasOpenNewTabIcon
                variant="monochrome"
                href={'https://support.iterable.com/hc/articles/4414807441556'}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('link.learnMore')}
              </Link>
            </span>
          </StyledBanner>
        )}
      </>
    );
  };

  return (
    <>
      {renderEditorSelector()}
      {contentEditor === HtmlEditor.Wysiwyg ? (
        <WYSIWYGEditor
          canLoadHTML
          canUseAutoCompleteParams
          id="content-editor-wysiwyg"
          initialValue={initialValue}
          lastSavedValue={lastSavedValue}
          onChange={onChange}
          value={value}
        />
      ) : (
        <SideBySideEditor
          canLoadHTML
          id="content-editor-side-by-side"
          name="snippet-editor-ace"
          onChange={onChange}
          value={value}
        />
      )}
    </>
  );
};
